import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Archive, FolderKanban, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";

import type { CommandAction } from "@/components/domain/CommandPalette";
import { useCommandPalette } from "@/components/domain/CommandPaletteProvider";
import { useRankedProjects } from "@/hooks/useRankedProjects";
import { apiGet } from "@/lib/api/client";
import { isSuperadminRole, useAuth } from "@/lib/auth/AuthContext";
import { queryKeys } from "@/lib/query/keys";

// Haengt Projekt-Actions in die CommandPalette: «Neues Projekt» und die
// ranked Projekt-Liste (recent > attention > active > archive) — die
// relevantesten zuerst. cmdk-Suche filtert client-seitig innerhalb dieses
// Sets. Die Seite «Projekte» selbst steht seit NAV-4 unter «Gehe zu»
// (Provider, aus der Leiste) — hier nicht ein zweites Mal (Prinzip 1).

//
// LEAD-27 (Daniel 08.10.2026: «sollen wider gefunden werden können»): die
// Suche findet auch, was weggeraeumt ist — klar gekennzeichnet («in der
// Ablage»), in einer eigenen Gruppe darunter. Enter fuehrt in die Ablage
// «Gelöscht» der Projektliste; dort steht «Wiederherstellen». Geladen wird
// erst, wenn die Palette aufgeht — schmal, eigener Endpunkt.

interface AblageResponse {
  ok: true;
  data: { projekte: readonly { id: string; title: string; code: string | null }[] };
}

const BUNDLE_TOKEN = "projects";
const MAX_PROJECT_ITEMS = 40;

export function useProjectPaletteActions(): void {
  const navigate = useNavigate();
  const { registerActions, open } = useCommandPalette();
  const { user } = useAuth();
  // Dieselbe Schranke wie das Business-Gate (RequireOperatingRole) — siehe
  // useCustomerPaletteActions.
  const operativ = !!user && !isSuperadminRole(user.role);
  const { data } = useRankedProjects();
  const ablage = useQuery({
    queryKey: queryKeys.leads.ablage(),
    enabled: operativ && open,
    queryFn: async () =>
      (await apiGet<AblageResponse>("/api/projects/ablage")).data.projekte,
    staleTime: 60_000,
  });

  const actions = useMemo<readonly CommandAction[]>(() => {
    if (!operativ) return [];
    const list: CommandAction[] = [
      {
        id: "projects:new",
        label: "Neues Projekt",
        group: "Aktionen",
        icon: Plus,
        onSelect: () => {
          void navigate("/projekte/neu");
        },
      },
    ];

    if (data) {
      const ordered = [
        ...data.recent,
        ...data.attention,
        ...data.active,
        ...data.archive,
      ].slice(0, MAX_PROJECT_ITEMS);
      for (const p of ordered) {
        list.push({
          id: `project:${p.id}`,
          label: p.code ? `${p.code} · ${p.title}` : p.title,
          group: "Projekte",
          icon: FolderKanban,
          onSelect: () => {
            void navigate(`/projekte/${p.id}`);
          },
        });
      }
    }

    for (const p of ablage.data ?? []) {
      list.push({
        id: `project-ablage:${p.id}`,
        label: p.code ? `${p.code} · ${p.title}` : p.title,
        description: "in der Ablage — öffnen und wiederherstellen",
        keywords: ["Ablage", "weggeräumt", "gelöscht", "wiederherstellen"],
        group: "Ablage",
        icon: Archive,
        onSelect: () => {
          void navigate("/projekte?ablage=geloescht");
        },
      });
    }

    return list;
  }, [data, ablage.data, navigate, operativ]);

  useEffect(() => {
    return registerActions(BUNDLE_TOKEN, actions);
  }, [registerActions, actions]);
}
